import React, { useEffect } from 'react';
import { Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';

const AppDownload = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "name": "Dine AI",
    "description": "Your personal AI dining assistant for discovering perfect meals with personalized recommendations based on your taste, dietary needs, and mood.",
    "operatingSystem": "iOS, Android",
    "applicationCategory": "LifestyleApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "500"
    },
    "downloadUrl": [
      "https://apps.apple.com/us/app/dine-ai/id6743492809",
      "https://play.google.com/store/apps/details?id=com.dine.ai&hl=en_US"
    ]
  };
  useEffect(() => {
    // Detect device and auto-redirect
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

    // Detect iOS
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
      // Redirect to App Store
      window.location.href = 'https://apps.apple.com/us/app/dine-ai/id6743492809';
    }
    // Detect Android
    else if (/android/i.test(userAgent)) {
      // Redirect to Play Store
      window.location.href = 'https://play.google.com/store/apps/details?id=com.dine.ai&hl=en_US';
    }
    // Desktop/Other - Show the page with both buttons
  }, []);

  return (
    <>
      <SEOHead
        title="Download Dine AI App - AI Dining Assistant | iOS & Android"
        description="Download Dine AI for iOS and Android. Your personal AI dining assistant for discovering perfect meals with personalized recommendations based on your taste, dietary needs, and mood."
        keywords="dine ai app download, ai dining app, restaurant app, food recommendation app, personalized menu app, ios android dining app"
        ogTitle="Download Dine AI - Your AI Dining Assistant"
        ogDescription="Get personalized meal recommendations with AI. Available on iOS and Android. Free to download."
        ogImage="/images/app-preview.png"
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Logo/Icon */}
        <div className="mb-8">
          <div className="w-24 h-24 bg-dineai-yellow rounded-3xl flex items-center justify-center mx-auto shadow-2xl">
            <Smartphone className="w-12 h-12 text-black" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl font-bold text-white mb-4">
          Download <span className="text-dineai-yellow">Dine AI</span>
        </h1>
        <p className="text-white/80 text-lg mb-8">
          Your personal AI dining assistant
        </p>

        {/* App Store Buttons */}
        <div className="space-y-4">
          {/* App Store */}
          <a
            href="https://apps.apple.com/us/app/dine-ai/id6743492809"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="w-full bg-dineai-yellow hover:bg-dineai-yellow/90 text-black py-6 text-lg rounded-xl shadow-lg">
              <div className="flex items-center justify-center gap-3">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs">Download on the</div>
                  <div className="text-sm font-semibold">App Store</div>
                </div>
              </div>
            </Button>
          </a>

          {/* Google Play Store */}
          <a
            href="https://play.google.com/store/apps/details?id=com.dine.ai&hl=en_US"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="w-full bg-dineai-yellow hover:bg-dineai-yellow/90 text-black py-6 text-lg rounded-xl shadow-lg">
              <div className="flex items-center justify-center gap-3">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs">GET IT ON</div>
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
              </div>
            </Button>
          </a>
        </div>

        {/* Footer text */}
        <p className="text-white/60 text-sm mt-8">
          Free to download and use
        </p>
      </div>
    </div>
    </>
  );
};

export default AppDownload;
